import { env } from "@moment/state";

/**
 * Tokens returned by the OAuth2 authorization server.
 */
export interface OAuthTokenPayload {
	idToken?: IdToken;
	accessToken?: string;
	refreshToken?: string;
}

/**
 * OAuth2 Connect ID token. The authorization server returns this token to provide information about
 * an authenticated user.
 */
export interface IdToken {
	given_name: string;
	family_name: string;
	nickname: string;
	name: string;
	picture: string;
	locale: string;
	updated_at: string;
	iss: string;
	sub: string;
	aud: string;
	iat: number;
	exp: number;
	claims: {
		[key: string]: string;
	};
	[key: string]: unknown;
}

export interface Auth0Info {
	auth0Domain: string;
	auth0ClientId: string;
	audience: string;
}

export const STAGING_DOCS_HOSTNAME = "docs-staging.moment.dev";
export const PROD_DOCS_HOSTNAME = "docs.moment.dev";

export const PROD_MOMENT_HOSTNAME = "www.moment.dev";
export const STAGING_MOMENT_HOSTNAME = "www.moment-staging.dev";
export const PROD_MARKETING_HOSTNAMES = new Set([PROD_MOMENT_HOSTNAME, "moment.dev"]);
export const LOCAL_MOMENT_HOSTNAME = "localhost:8787";

export const LOCAL_BASE_URL_DEFAULT = "http://localhost:3001";
const STAGING_BASE_URL_DEFAULT = `https://${STAGING_DOCS_HOSTNAME}`;
const PROD_BASE_URL_DEFAULT = `https://${PROD_DOCS_HOSTNAME}`;

export const STAGING_IFRAME_BASE_URL = "https://iframe-staging.moment.dev";
const PROD_IFRAME_BASE_URL = "https://iframe.moment.dev";

export const currentBaseUrl =
	typeof window === "undefined"
		? undefined
		: `${window.location.protocol}//${window.location.host}`;

export const isServiceWorkerDisabled = () => {
	return process.env.DISABLE_SERVICE_WORKER === "true";
};

/**
 * Returns the base URL for the parent frame
 * based on the provided app env.
 */
export const getParentFrameOriginBaseUrl = (env: MomentAppEnv): string => {
	let parentFrameBaseUrl = LOCAL_BASE_URL_DEFAULT;

	switch (env) {
		case "staging":
			parentFrameBaseUrl = STAGING_BASE_URL_DEFAULT;
			break;
		case "production":
			parentFrameBaseUrl = PROD_BASE_URL_DEFAULT;
			break;
	}

	return parentFrameBaseUrl;
};

export const getDeepLinkProtocol = (env?: MomentAppEnv): string => {
	switch (env) {
		case "production":
			return "moment-app://-";
		case "staging":
			return "moment-app-staging://-";
		default:
			return "moment-app-dev://-";
	}
};

export const getMarketingSiteBaseURL = (appEnv?: MomentAppEnv): string => {
	switch (appEnv) {
		case "production":
			return `https://${PROD_MOMENT_HOSTNAME}`;
		case "staging":
			return `https://${STAGING_MOMENT_HOSTNAME}`;
		case "local":
		case "testing":
		default:
			return "http://localhost:3000";
	}
};

export const getDeepLinkLandingBaseURL = (appEnv?: MomentAppEnv): string => {
	return getMarketingSiteBaseURL(appEnv);
};

export const getDocumentDeepLinkLandingURL = (
	appEnv: MomentAppEnv | undefined,
	workspaceSlug: string,
	documentId: string,
	pageSegments: string[] = []
): string => {
	const encodedPath = [workspaceSlug, documentId, ...pageSegments]
		.map((segment) => encodeURIComponent(segment))
		.join("/");

	return `${getDeepLinkLandingBaseURL(appEnv)}/deeplink/${encodedPath}`;
};

export const getWorkspaceDeepLinkLandingURL = (
	appEnv: MomentAppEnv | undefined,
	workspaceId: string
): string => {
	return `${getDeepLinkLandingBaseURL(appEnv)}/deeplink/workspaces/${encodeURIComponent(workspaceId)}`;
};

export const getWebBaseURL = (appEnv?: MomentAppEnv): string => {
	if (env.isDesktopApp || env.isMobileApp) {
		switch (appEnv) {
			case "production":
				return "moment-app://-";
			case "staging":
				return "moment-app-staging://-";
			default:
				return "moment-app-dev://-";
		}
	}

	// On local, force to local urls (instead of passed env)
	if (currentBaseUrl === LOCAL_BASE_URL_DEFAULT) {
		return LOCAL_BASE_URL_DEFAULT;
	}

	switch (appEnv) {
		case "staging":
			// Allow current urls for vercel deploys, otherwise use web-staging
			return currentBaseUrl ?? STAGING_BASE_URL_DEFAULT;
		case "production":
			return currentBaseUrl ?? PROD_BASE_URL_DEFAULT;
		case "local":
		case "testing":
		default:
			return currentBaseUrl ?? LOCAL_BASE_URL_DEFAULT;
	}
};

export const getAuthorizationParams = () => {
	return {
		redirect_uri: getLoginRedirectURL(env.appEnv as MomentAppEnv),
		scope: "openid profile email offline_access",
		audience: env.authSettings.audience,
	};
};

/**
 * Get the base URL for the tldraw sync server based on the environment.
 *
 * Set `TLDRAW_SYNC_URL` env var to override (useful for local development).
 */
export const getTldrawBaseUrl = (env: MomentAppEnv, useWs: boolean = true): string => {
	if (process.env["TLDRAW_SYNC_URL"] !== undefined) {
		return process.env["TLDRAW_SYNC_URL"] ?? "";
	}

	switch (env) {
		// case "local": {
		// 	// Use localhost wrangler dev server for local development
		// 	const protocol = useWs ? "ws:" : "http:";
		// 	return `${protocol}//localhost:5173`;
		// }
		case "local":
		case "staging": {
			const protocol = useWs ? "wss:" : "https:";
			return `${protocol}//tldraw-worker-staging.moment.dev`;
		}
		case "production": {
			const protocol = useWs ? "wss:" : "https:";
			return `${protocol}//tldraw-worker-production.moment.dev`;
		}
		default: {
			console.warn(`WARNING: no tldraw sync url defined for env ${process.env["APP_ENV"]}`);
			const protocol = useWs ? "ws:" : "http:";
			return `${protocol}//localhost:5173`; // default to local
		}
	}
};

export const getIframeBaseUrl = (env?: MomentAppEnv): string => {
	// Use the commit-specific URL as the iframe URL
	// for Vercel preview pages. This is just to
	// demonstrate that the app running on `*.vercel.app`
	// can load an iframe of a different origin but
	// still belonging to the `vercel.app` root domain.
	if (
		process.env.VERCEL_URL &&
		process.env.VERCEL_GIT_COMMIT_REF !== "main" &&
		process.env.VERCEL_GIT_COMMIT_REF !== "release-app"
	) {
		return `https://${process.env.VERCEL_URL}`;
	}

	switch (env) {
		case "staging":
			return STAGING_IFRAME_BASE_URL;
		case "production":
			return PROD_IFRAME_BASE_URL;
		case "local":
			return LOCAL_BASE_URL_DEFAULT;
		case "testing":
		default:
			return LOCAL_BASE_URL_DEFAULT;
	}
};

export const getLoginRedirectURL = (env: MomentAppEnv): string => {
	return `${getWebBaseURL(env)}/login`;
};

const getLogoutRedirectURL = (env: MomentAppEnv): string => {
	return `${getWebBaseURL(env)}/logout`;
};

const getUserHMACURL = (env: MomentAppEnv): string => {
	return `${getWebBaseURL(env)}/api/v1/user/hmac`;
};

export const getAuthURLs = (env: MomentAppEnv) => {
	const loginRedirectUri = getLoginRedirectURL(env);
	const logoutRedirectUri = getLogoutRedirectURL(env);
	const userHmacUrl = getUserHMACURL(env);

	return {
		loginRedirectUri,
		logoutRedirectUri,
		userHash: userHmacUrl,
	};
};
